// Mobs: Slimes, Noob Zombies, Boom Bots and Flyers. Bonk them with the sword or stomp on them.
// Explore also has the Mossy Giant: too big to squash, so it is beaten with swords and arrows.
import * as THREE from 'three';
import { moveBody, boxFree } from './physics.js';
import { slimeModel, boomBotModel, flyerModel, nameTag } from './models.js';
import { golemModel } from './boss.js';
import { Character } from './character.js';
import { paintNPC } from './skins.js';
import { BLOCKS } from './blocks.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { disposeObj } from './entities.js';

const _v = new THREE.Vector3();
let zombieSkin = null;

// Mossy Giant tuning: big but beatable. It chases slowly, and when the player is close it raises its arms
// (windup), then stomps a shockwave that costs a heart to anyone on the ground nearby. Jumping over it dodges.
export const GIANT = {
  hp: 10,
  hw: 1, // hitbox half-width (arrows and swords use this body)
  h: 5.2,
  chase: 16, // blocks: follows the player within this range
  run: 1.4, // chase speed, blocks per second
  walk: 1, // walking home
  reach: 3.2, // starts a stomp when the player is this close
  windup: 0.45, // seconds with arms raised before the stomp lands
  radius: 3.4, // shockwave reach
  cooldown: 2.6, // seconds between stomps
};
const GIANT_TILES = { top: 'grass_top', body: 'cobble', front: 'cobble', head: 'cobble', limb: 'stone_brick', eye: '#9cff7a' };

export class Mob {
  constructor(level, kind, x, y, z, opts = {}) {
    this.level = level;
    this.kind = kind;
    this.dead = false;
    this.body = { pos: new THREE.Vector3(x, y, z), vel: new THREE.Vector3(), hw: 0.38, h: 0.8, onGround: false, self: null };
    this.home = new THREE.Vector3(x, y, z);
    this.hp = 1;
    this.t = Math.random() * 3;
    this.timer = 0.5 + Math.random();
    this.flash = 0;
    this.facing = Math.random() * Math.PI * 2;
    this.dying = 0;
    const g = new THREE.Group();
    if (kind === 'slime') {
      const c = opts.slimeColor || ['#6fd65a', '#3f9a32'];
      this.model = slimeModel(c[0], c[1], 0.85);
      this.body.hw = 0.42;
      this.body.h = 0.85;
    } else if (kind === 'zombie') {
      if (!zombieSkin) zombieSkin = paintNPC('zombie');
      this.ch = new Character(zombieSkin, { scale: 0.95 });
      this.model = this.ch.root;
      this.body.hw = 0.3;
      this.body.h = 1.7;
      this.hp = 2;
    } else if (kind === 'boom') {
      this.model = boomBotModel();
      this.body.hw = 0.33;
      this.body.h = 0.95;
      this.fuse = -1;
    } else if (kind === 'giant') {
      this.golem = golemModel(GIANT_TILES);
      this.model = this.golem.group;
      // A mossy green tint, a little brighter than plain stone (the golem owns this copy of the block material).
      this.model.traverse((o) => o.material?.map && o.material.color.setRGB(0.92, 1.16, 0.86));
      this.body.hw = GIANT.hw;
      this.body.h = GIANT.h;
      this.hp = GIANT.hp;
      this.windup = -1; // seconds into a stomp's windup, or -1
      this.stompCD = 0;
      this.stomps = 0;
      this.slam = 0; // arms slam down for a moment after a stomp
      this.lift = 0; // the model eases up after a one-block step
      this.bounced = 0; // no contact damage just after bouncing off its head
    } else {
      this.model = flyerModel(opts.flyerKind || 'fire');
      this.body.hw = 0.3;
      this.body.h = 0.5;
      this.flyer = true;
    }
    g.add(this.model);
    this.obj = g;
    level.group.add(g);
    this.mats = [];
    g.traverse((o) => o.material && this.mats.push(o.material));
    if (this.golem) {
      // Its world tag rides above its head (added after mats, so hit flashes leave the tag alone).
      this.tag = nameTag('MOSSY GIANT', { color: '#c9ff9e', crown: false, scale: 2.2 });
      this.tag.name = 'MOSSY GIANT';
      this.tag.position.y = GIANT.h + 0.8;
      g.add(this.tag);
    }
  }

  get center() {
    return _v.set(this.body.pos.x, this.body.pos.y + this.body.h / 2, this.body.pos.z);
  }

  groundAhead(dx, dz) {
    const W = this.level.world;
    const x = Math.floor(this.body.pos.x + dx), z = Math.floor(this.body.pos.z + dz);
    const y = Math.floor(this.body.pos.y);
    for (let k = 1; k <= 3; k++) {
      const id = W.get(x, y - k, z);
      if (id && BLOCKS[id].solid && !BLOCKS[id].hurt) return true;
      if (id && BLOCKS[id].liquid) return false;
    }
    return false;
  }

  hit(dir, dmg = 1) {
    if (this.dying) return;
    this.hp -= dmg;
    this.flash = 0.25;
    // A giant is too heavy to knock back: it only staggers for a moment.
    if (this.golem) this.stun = Math.max(this.stun || 0, 0.2);
    else {
      this.body.vel.x += dir.x * 7;
      this.body.vel.z += dir.z * 7;
      this.body.vel.y = Math.max(this.body.vel.y, 5);
    }
    audio.play('hit');
    this.level.fx.particles.burst(this.center, { count: 8, colors: ['#ffffff', '#ffe08a'], speed: 3, up: 3, size: 0.09, glow: true });
    if (this.kind === 'boom') this.fuse = -1;
    if (this.hp <= 0) this.die(false);
  }

  die(stomped) {
    if (this.dying) return;
    this.dying = 0.001;
    const L = this.level;
    const c = this.center.clone();
    const cols = this.kind === 'slime' ? ['#7be35a', '#3f9a32', '#c8ffb0'] : this.kind === 'zombie' ? ['#b9c94a', '#1d69ac', '#93ad3c'] : this.kind === 'boom' ? ['#b8402e', '#ffd23f', '#2a2a30'] : this.golem ? ['#8f9b7c', '#9cff7a', '#6d7275'] : ['#ff8a20', '#5a1a10', '#ffd040'];
    L.fx.particles.burst(c, { count: this.golem ? 60 : 22, colors: cols, speed: 5, up: 6, size: 0.17, life: 0.9, gravity: 18 });
    L.fx.rings.add(new THREE.Vector3(c.x, this.body.pos.y, c.z), { color: '#ffffff', to: this.golem ? 4.5 : 2.2, life: 0.4 });
    audio.play(this.kind === 'slime' ? 'squish' : 'mobDie');
    L.onMobDefeated(this, stomped);
    // A giant's coins come in its own loot bundle (onMobDefeated), so it drops no extra ones here.
    const n = this.golem ? 0 : 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < n; i++) L.spawnLoot(c, 'coin');
  }

  explode() {
    const L = this.level;
    const c = this.center.clone();
    L.fx.particles.burst(c, { count: 40, colors: ['#ff8a20', '#ffd040', '#5a5a5a', '#ffffff'], speed: 9, up: 7, size: 0.22, life: 1.0, gravity: 10, glow: true, bright: 1.4 });
    L.fx.rings.add(new THREE.Vector3(c.x, this.body.pos.y, c.z), { color: '#ffb040', to: 5, life: 0.5 });
    audio.play('explosion');
    haptics.play('explosion');
    L.shake(0.6);
    const pl = L.player;
    const d = pl.pos.distanceTo(this.body.pos);
    if (d < 3.4) pl.hurt(1, this.body.pos, 8);
    this.dying = 0.001;
    this.exploded = true;
  }

  update(dt) {
    const L = this.level, pl = L.player, b = this.body;
    this.t += dt;
    if (this.dying) {
      this.dying += dt;
      const s = Math.max(0.01, 1 - this.dying * 6);
      this.obj.scale.set(s * 1.3, s * 0.4, s * 1.3);
      if (this.dying > 0.18) this.dead = true;
      return;
    }
    if (b.pos.y < L.killY) {
      this.dead = true;
      return;
    }
    const toP = _v.set(pl.pos.x - b.pos.x, 0, pl.pos.z - b.pos.z);
    const dist = toP.length();
    const dy = pl.pos.y - b.pos.y;
    if (this.stun > 0) this.stun -= dt;
    const near = dist < (this.golem ? GIANT.chase : 9) && Math.abs(dy) < 4 && !pl.dead && !(this.stun > 0);
    let wantX = 0, wantZ = 0, speed = 0;

    if (this.kind === 'slime') {
      if (b.onGround) {
        b.vel.x *= 0.6;
        b.vel.z *= 0.6;
        if (!(this.stun > 0)) this.timer -= dt;
        if (this.timer <= 0) {
          let dir;
          if (near) dir = toP.clone().normalize();
          else {
            const a = Math.random() * Math.PI * 2;
            dir = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
            const back = new THREE.Vector3(this.home.x - b.pos.x, 0, this.home.z - b.pos.z);
            if (back.length() > 3) dir = back.normalize();
          }
          if (!this.groundAhead(dir.x * 1.6, dir.z * 1.6)) dir.multiplyScalar(-1);
          if (this.groundAhead(dir.x * 1.6, dir.z * 1.6)) {
            b.vel.set(dir.x * 3.3, 7.2, dir.z * 3.3);
            this.facing = Math.atan2(dir.x, dir.z);
            if (dist < 14) audio.play('step_slime', { pitch: 1.3 });
          }
          this.timer = near ? 0.8 + Math.random() * 0.5 : 1.2 + Math.random() * 1.2;
        }
      }
      // squash & stretch
      const sy = b.onGround ? 1 - Math.max(0, Math.sin(this.t * 6)) * 0.08 : 1 + Math.min(0.3, Math.abs(b.vel.y) * 0.04);
      const sxz = 1 / Math.sqrt(sy);
      this.model.scale.set(sxz, sy, sxz);
    } else if (this.kind === 'zombie') {
      if (near) {
        wantX = toP.x / (dist || 1);
        wantZ = toP.z / (dist || 1);
        speed = 1.9;
      } else {
        const back = new THREE.Vector3(this.home.x - b.pos.x, 0, this.home.z - b.pos.z);
        if (back.length() > 2) {
          back.normalize();
          wantX = back.x;
          wantZ = back.z;
          speed = 1.1;
        }
      }
      if (speed > 0 && !this.groundAhead(wantX * 0.7, wantZ * 0.7)) speed = 0;
      if (Math.random() < dt * 0.15 && dist < 12) audio.play('groan');
    } else if (this.kind === 'boom') {
      if (this.fuse >= 0) {
        this.fuse += dt;
        const blink = Math.sin(this.fuse * (8 + this.fuse * 18)) > 0;
        this.setGlow(blink ? 0.9 : 0);
        const sw = 1 + this.fuse * 0.25;
        this.model.scale.set(sw, sw, sw);
        if (dist > 5.5) {
          this.fuse = -1;
          this.setGlow(0);
          this.model.scale.set(1, 1, 1);
        } else if (this.fuse > 1.35) {
          this.explode();
          return;
        }
      } else if (near) {
        wantX = toP.x / (dist || 1);
        wantZ = toP.z / (dist || 1);
        speed = 2.5;
        if (dist < 2.2) {
          this.fuse = 0;
          audio.play('fuse');
        }
      }
      if (speed > 0 && !this.groundAhead(wantX * 0.6, wantZ * 0.6)) speed = 0;
      const legs = this.model.userData.legs;
      if (legs) legs.forEach((l, i) => (l.position.y = (1.5 + (speed ? Math.abs(Math.sin(this.t * 12 + i * Math.PI)) * 1.2 : 0)) / 13));
    } else if (this.golem) {
      // Mossy Giant: plods after the player, plants its feet and stomps when they come close, otherwise walks home.
      this.stompCD = Math.max(0, this.stompCD - dt);
      if (this.windup >= 0) {
        // A long stun (a finished lesson, a frost sword) calls the stomp off; a hit's stagger only pauses it.
        if (this.stun > 0.3) this.windup = -1;
        else if (!(this.stun > 0) && (this.windup += dt) >= GIANT.windup) this.stomp();
      } else if (near && dist < GIANT.reach && b.onGround && this.stompCD <= 0) {
        this.windup = 0;
        this.facing = Math.atan2(toP.x, toP.z);
      } else if (near && dist > 1) {
        wantX = toP.x / dist;
        wantZ = toP.z / dist;
        speed = GIANT.run;
      } else if (!near && !(this.stun > 0)) {
        // Walks home while the player is away (a hit's stagger keeps it still for a moment).
        const back = new THREE.Vector3(this.home.x - b.pos.x, 0, this.home.z - b.pos.z);
        if (back.length() > 2) {
          back.normalize();
          wantX = back.x;
          wantZ = back.z;
          speed = GIANT.walk;
        }
      }
      if (speed > 0 && !this.groundAhead(wantX * 1.3, wantZ * 1.3)) speed = 0;
    } else {
      // flyer: hover around home, swoop at the player
      const hover = new THREE.Vector3(this.home.x + Math.sin(this.t * 0.7) * 2, this.home.y + Math.sin(this.t * 1.3) * 0.5, this.home.z + Math.cos(this.t * 0.6) * 2);
      let target = hover;
      if (near && dist < 7) {
        this.swoop = (this.swoop || 0) + dt;
        if (this.swoop < 2.4) target = new THREE.Vector3(pl.pos.x, pl.pos.y + 1.1, pl.pos.z);
        else if (this.swoop > 3.6) this.swoop = 0;
      }
      const d = target.sub(b.pos);
      const len = d.length();
      if (len > 0.05) d.multiplyScalar(Math.min(1, len) / len);
      b.vel.lerp(d.multiplyScalar(4.2), Math.min(1, dt * 3));
      b.pos.addScaledVector(b.vel, dt);
      if (b.vel.lengthSq() > 0.1) this.facing = Math.atan2(b.vel.x, b.vel.z);
      const w = this.model.userData.wings;
      if (w) {
        w[0].rotation.z = Math.sin(this.t * 18) * 0.7;
        w[1].rotation.z = -Math.sin(this.t * 18) * 0.7;
      }
    }

    if (!this.flyer) {
      if (speed > 0) {
        b.vel.x += (wantX * speed - b.vel.x) * Math.min(1, dt * 8);
        b.vel.z += (wantZ * speed - b.vel.z) * Math.min(1, dt * 8);
        this.facing = Math.atan2(wantX, wantZ);
      } else if (b.onGround && this.kind !== 'slime') {
        b.vel.x *= Math.exp(-10 * dt);
        b.vel.z *= Math.exp(-10 * dt);
      }
      b.vel.y = Math.max(-30, b.vel.y - 28 * dt);
      moveBody(b, L.world, L.colliders, b.vel.x * dt, b.vel.y * dt, b.vel.z * dt);
      if (this.golem && speed > 0 && b.hitWall && b.onGround) this.stepUp(wantX, wantZ);
    }

    // face direction
    let diff = this.facing - this.obj.rotation.y;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.obj.rotation.y += diff * Math.min(1, dt * 8);
    this.obj.position.copy(b.pos);
    if (this.ch) this.ch.update(dt, { speed: Math.hypot(b.vel.x, b.vel.z), grounded: b.onGround, zombie: true });
    if (this.golem) this.animateGiant(dt, speed > 0);

    if (this.flash > 0) {
      this.flash -= dt;
      this.setGlow(this.flash > 0 ? 0.8 : 0, '#ffffff');
    }

    // contact with player (landing on a giant always bounces, even while invulnerable or after a hit)
    if (this.bounced > 0) this.bounced -= dt;
    if (pl.dead || (!this.golem && (pl.invuln > 0 || this.stun > 0))) return;
    const ph = pl.h;
    const overlap = Math.abs(pl.pos.x - b.pos.x) < pl.hw + b.hw && Math.abs(pl.pos.z - b.pos.z) < pl.hw + b.hw && pl.pos.y < b.pos.y + b.h && pl.pos.y + ph > b.pos.y;
    if (!overlap) return;
    const fromAbove = pl.vel.y < -0.5 && pl.pos.y > b.pos.y + b.h * 0.55;
    if (this.golem) {
      if (fromAbove) this.bounceOff(pl);
      else if (!(pl.invuln > 0 || this.stun > 0 || this.bounced > 0)) pl.hurt(1, b.pos);
      return;
    }
    if (fromAbove) {
      pl.vel.y = 11;
      pl.jumpsUsed = 1;
      haptics.play('stomp');
      this.die(true);
      audio.play('squish');
      L.fx.worldUI.float('STOMP!', this.center.clone().add(new THREE.Vector3(0, 0.6, 0)), 'ft-gold');
      return;
    }
    pl.hurt(1, b.pos);
  }

  // ---------- Mossy Giant ----------
  // The stomp lands: a shockwave ring, a rumble and one heart from anyone standing on the ground nearby.
  // Jumping over it is the dodge. The hurt buzz goes first so it wins the frame; otherwise the stomp buzzes.
  stomp() {
    const L = this.level, pl = L.player, c = this.body.pos.clone();
    this.windup = -1;
    this.stompCD = GIANT.cooldown;
    this.slam = 0.3;
    this.stomps++;
    if (pl.onGround && !pl.dead && Math.hypot(pl.pos.x - c.x, pl.pos.z - c.z) < GIANT.radius && Math.abs(pl.pos.y - c.y) < 1.6) pl.hurt(1, c, 8);
    audio.play('stomp');
    haptics.play('stomp');
    L.game.camRig.shake(0.4);
    L.fx.rings.add(c, { color: '#9cff7a', from: 0.6, to: GIANT.radius, life: 0.45, opacity: 1, bright: 1.8 });
    L.fx.rings.add(c, { color: '#fff1b0', from: 0.3, to: GIANT.radius - 0.25, life: 0.45, opacity: 0.6 });
    L.fx.particles.dust(c, 18, '#a6b38c');
  }

  // Too big to squash: landing on its head bounces the player off, and nobody is hurt.
  bounceOff(pl) {
    const b = this.body, away = new THREE.Vector3(pl.pos.x - b.pos.x, 0, pl.pos.z - b.pos.z);
    if (away.lengthSq() < 0.01) away.set(Math.sin(pl.facing), 0, Math.cos(pl.facing));
    pl.knock(away.normalize(), 6, 11);
    pl.jumpsUsed = 1;
    this.bounced = 0.4;
    audio.play('bounce');
    haptics.play('bounce');
    this.level.fx.worldUI.float('Too big to squash!', new THREE.Vector3(b.pos.x, b.pos.y + b.h + 0.6, b.pos.z), 'ft-green');
  }

  // Giants step up one block instead of stalling at every ledge; the model eases up after the body.
  stepUp(dx, dz) {
    const b = this.body, W = this.level.world, y = b.pos.y + 1;
    const free = (x, z) => boxFree(W, x - b.hw, y, z - b.hw, x + b.hw, y + b.h, z + b.hw);
    if (!free(b.pos.x, b.pos.z) || !free(b.pos.x + dx * 0.25, b.pos.z + dz * 0.25)) return;
    b.pos.y = y;
    this.lift = 1;
  }

  animateGiant(dt, walking) {
    const m = this.golem, k = Math.min(1, dt * 8), raised = this.windup >= 0, slam = this.slam > 0;
    if (this.lift > 0) {
      this.obj.position.y -= this.lift;
      this.lift = Math.max(0, this.lift - dt * 5);
    }
    const sw = walking ? Math.sin(this.t * 4.5) * 0.45 : 0;
    m.legL.rotation.x += (sw - m.legL.rotation.x) * k;
    m.legR.rotation.x += (-sw - m.legR.rotation.x) * k;
    // Arms swing while walking, rise over its head during the windup and slam down as it stomps.
    const arm = raised ? -2.6 : slam ? 0.4 : walking ? -sw * 0.6 : Math.sin(this.t * 1.5) * 0.08;
    m.armL.rotation.x += (arm - m.armL.rotation.x) * (slam ? 1 : k);
    m.armR.rotation.x += ((raised || slam ? arm : -arm) - m.armR.rotation.x) * (slam ? 1 : k);
    m.head.rotation.x += ((raised ? -0.3 : 0) - m.head.rotation.x) * k;
    this.slam = Math.max(0, this.slam - dt);
  }

  // Back to a calm, unflashed giant (it respawns after its cooldown).
  calm() {
    this.windup = -1;
    this.stompCD = 0;
    this.slam = 0;
    this.lift = 0;
    this.bounced = 0;
    this.flash = 0;
    this.setGlow(0);
  }

  setGlow(k, color = '#ffffff') {
    for (const m of this.mats) {
      if (this.golem) {
        // The giant's own block material has a glow map, so its flash over-brightens the colour instead.
        if (!m.color) continue;
        m.userData.baseColor ??= m.color.clone();
        if (k > 0) m.color.setRGB(2.4, 2.4, 2.4);
        else m.color.copy(m.userData.baseColor);
        continue;
      }
      if (!m.emissive) continue;
      if (m.userData.baseEmissive === undefined) {
        m.userData.baseEmissive = m.emissive.clone();
        m.userData.baseIntensity = m.emissiveIntensity;
      }
      if (k > 0) {
        m.emissive.set(color);
        m.emissiveIntensity = k;
      } else {
        m.emissive.copy(m.userData.baseEmissive);
        m.emissiveIntensity = m.userData.baseIntensity;
      }
    }
  }

  dispose() {
    this.level.group.remove(this.obj);
    this.tag?.material.map?.dispose();
    if (this.ch) this.ch.dispose();
    else disposeObj(this.obj);
  }
}
